<template>
    <BaseCard padding="md" class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
        <BaseNotification v-if="deleteError" theme="error" :text="deleteError" class="sm:basis-full" hide-dismiss />
        <BaseNotification v-else-if="extendNotice" :theme="extendNotice.theme" :text="extendNotice.text" class="sm:basis-full" hide-dismiss />
        <div class="flex items-center gap-3 min-w-0 flex-1">
            <ReflectionIcon v-if="showIcon" :kind="task.is_self_assessment_only ? 'self' : 'sixty'" size="md" />
            <div class="flex flex-col gap-1 min-w-0">
                <div class="body-semibold text-ink">{{ t("reflections.self.task.heading", { action: isStarted ? "resume" : "start", title: task.title }) }}</div>
                <div v-if="task.subtitle" class="scaffold-body-small text-ink-muted">{{ task.subtitle }}</div>
                <div v-if="progress" class="scaffold-details-small text-ink-muted">{{ progress }}</div>
                <div v-if="closes" class="scaffold-details-small text-ink-muted">{{ closes }}</div>
            </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 self-start sm:self-auto sm:shrink-0">
            <a
                ref="startLink"
                :href="href"
                :aria-label="t('reflections.shared.actionLabel', { action: actionLabel, target: task.title })"
                :aria-disabled="deleting"
                class="base-btn-neutral base-btn-sm whitespace-nowrap text-center"
                :class="{ 'pointer-events-none opacity-50': deleting }"
                @click.prevent="onStart">
                {{ actionLabel }}
            </a>
            <BaseButton
                v-if="canExtend"
                theme="tertiary"
                size="sm"
                :aria-label="t('reflections.shared.actionLabel', { action: t('reflections.self.task.extend'), target: task.title })"
                :disabled="extending || deleting"
                @click="onExtend">
                {{ extending ? t("reflections.self.task.extendBusy") : t("reflections.self.task.extend") }}
            </BaseButton>
            <BaseButton
                v-if="task.can_delete"
                theme="tertiary"
                size="sm"
                :aria-label="t('reflections.shared.actionLabel', { action: t('common.delete'), target: task.title })"
                :disabled="deleting"
                @click="showDeleteConfirm = true">
                {{ t("common.delete") }}
            </BaseButton>
        </div>
    </BaseCard>
    <ConfirmationDialog
        v-if="task.can_delete"
        v-model:open="showDeleteConfirm"
        :title="task.is_self_assessment_only ? t('reflections.shared.deleteCycle.titleSelf') : t('reflections.shared.deleteCycle.title360')"
        :text="deleteText"
        :confirm-label="t('common.delete')"
        confirm-theme="danger"
        :confirm-disabled="deleting"
        @confirm="onConfirmDelete" />
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleForSubject, PendingPersonalTask } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import { closesLabel, formatDate } from "~vue/utils/threeSixty";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    task: PendingPersonalTask;
    // The icon disambiguates task kinds when the Upcoming tab mixes 360, self,
    // and team-feedback rows. Inside a single-kind tab (360s / Self Reflection),
    // the section header already provides that context, so the icon is redundant.
    showIcon?: boolean;
}

const { task, showIcon = true } = defineProps<Props>();
const emit = defineEmits<{
    (event: "start", task: PendingPersonalTask): void;
    (event: "deleted", cycleId: string): void;
    // The extend response re-serializes the cycle; the parent copies its
    // deadline fields into the pending-task entry it owns.
    (event: "extended", cycle: CycleForSubject): void;
}>();

const { t } = useI18n();

const showDeleteConfirm = ref(false);
const deleting = ref(false);
const deleteError = ref<string | null>(null);

// A draft 360 hasn't emailed anyone yet (invites go out at launch), so the
// only thing lost is the subject's own setup.
const deleteText = computed(() => (task.is_self_assessment_only ? t("reflections.shared.deleteCycle.bodySelf") : t("reflections.shared.deleteCycle.bodyDraft")));

function onStart() {
    if (deleting.value) {
        return;
    }
    emit("start", task);
}

async function onConfirmDelete() {
    if (deleting.value) {
        return;
    }
    deleting.value = true;
    deleteError.value = null;
    // 204 with no body on success, so don't ask for JSON.
    const { error } = await useFetch(`/v1/reflections/cycles/${task.id}`).delete();
    deleting.value = false;
    showDeleteConfirm.value = false;
    if (error.value) {
        deleteError.value = error.value.message || t("reflections.shared.deleteCycle.failed");
        return;
    }
    emit("deleted", task.id);
}

const closes = computed(() => closesLabel(task.closes_at));

// A self-launched self reflection owns its deadline: the server grants it
// exactly one +14-day extension (capped at launch + 28 days). Campaign cycles
// inherit the org's deadline and carry no extension_limit_at, so they never
// show the action; an extension already used leaves the cap hit and the row
// quietly drops it.
const EXTENSION_DAYS = 14;
const extending = ref(false);
const extendNotice = ref<{ theme: "error" | "info" | "success"; text: string } | null>(null);
const startLink = ref<HTMLAnchorElement | null>(null);
// Flipped when the server says the deadline lapsed between render and click.
const extendClosed = ref(false);
const closesAt = computed(() => (task.closes_at ? new Date(task.closes_at) : null));
const extensionLimitAt = computed(() => (task.extension_limit_at ? new Date(task.extension_limit_at) : null));
const atExtensionLimit = computed(() => Boolean(closesAt.value && extensionLimitAt.value && closesAt.value >= extensionLimitAt.value));
const canExtend = computed(
    () =>
        task.kind === "self_reflection_cycle" &&
        closesAt.value !== null &&
        extensionLimitAt.value !== null &&
        !extendClosed.value &&
        !atExtensionLimit.value &&
        closesAt.value > new Date(),
);

async function onExtend() {
    if (extending.value) {
        return;
    }
    extending.value = true;
    // A failed delete's error would otherwise sit in front of this action's outcome.
    deleteError.value = null;
    extendNotice.value = null;
    const { data, error } = await useFetch(`/v1/reflections/cycles/${task.id}/extend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: EXTENSION_DAYS }),
    }).json<CycleEnvelopeResponse>();
    extending.value = false;
    if (error.value || !data.value?.cycle) {
        const code = (data.value as { code?: string } | null)?.code;
        if (code === "cycle_extension_limit_reached" || code === "cycle_not_in_state") {
            // Either way there is nothing left to extend: hide the action and
            // say why rather than offer a button that always fails.
            extendClosed.value = true;
            extendNotice.value = { theme: "info", text: t("reflections.self.task.extendClosed") };
            return;
        }
        extendNotice.value = { theme: "error", text: t("reflections.self.task.extendFailed") };
        return;
    }
    extendNotice.value = { theme: "success", text: t("reflections.self.task.extended", { date: formatDate(data.value.cycle.closes_at, "short") }) };
    emit("extended", data.value.cycle);
    // The spent extension takes its button with it, so focus lands on the
    // row's remaining action instead of falling off the page.
    await nextTick();
    startLink.value?.focus();
}

// Campaign tasks have no cycle yet, so they report no progress and keep
// offering "Start". Everything else can tell the user their answers are still
// there, which is the whole point of the counts.
const answeredCount = computed(() => task.answered_count ?? 0);
const questionCount = computed(() => task.question_count ?? 0);
const isStarted = computed(() => answeredCount.value > 0);
const actionLabel = computed(() => (isStarted.value ? t("reflections.self.task.resume") : t("reflections.self.task.start")));

const progress = computed(() => {
    if (questionCount.value === 0) {
        return "";
    }
    return isStarted.value
        ? t("reflections.self.task.progressAnswered", { answered: answeredCount.value, total: questionCount.value })
        : t("reflections.self.task.questionCount", { count: questionCount.value });
});

const href = computed(() => {
    if (task.kind === "self_reflection_cycle") {
        return `/reflections/360/${task.id}/self`;
    }
    if (task.kind === "self_reflection_campaign") {
        return `/reflections/360/start?mode=self&campaign_id=${task.id}`;
    }
    if (task.kind === "cycle_setup_campaign") {
        return `/reflections/360/start?campaign_id=${task.id}`;
    }
    return `/reflections/360/${task.id}/nominate`;
});
</script>
